import type { DirectoryHandle } from "idfweb-project";

/**
 * A directory on the user's computer as a project source: the browser's side
 * of it. The File System Access API is Chromium-only, so
 * `localDirectoriesSupported` decides whether the page offers this at all. The
 * sync itself is `DirectorySync` in idfweb-project, run by the runtime worker.
 */

/** Whether this browser has the File System Access API. */
export function localDirectoriesSupported(): boolean {
    return typeof picker() === "function";
}

/**
 * The directory the user chose, with read access only unless `write`, or
 * undefined if they dismissed the picker. Write access to a folder being
 * opened is asked for separately, once the user has chosen to sync.
 */
export async function pickDirectory(write = false): Promise<DirectoryHandle | undefined> {
    const show = picker();
    if (show === undefined) throw new Error("this browser cannot open a local directory");
    try {
        return await show({ id: "idfweb-project", mode: write ? "readwrite" : "read" });
    } catch (e) {
        if (e instanceof DOMException && e.name === "AbortError") return undefined;
        throw e;
    }
}

/**
 * Whether the directory may be read, and written when `write` is set, asking
 * the user if it may not. A handle recalled from storage keeps no permission
 * across a visit, and the prompt needs a click shortly before it.
 */
export async function allowedAccess(handle: DirectoryHandle, write: boolean): Promise<boolean> {
    const mode = { mode: write ? "readwrite" : "read" } as const;
    if (handle.queryPermission === undefined) return true;
    if ((await handle.queryPermission(mode)) === "granted") return true;
    return (await handle.requestPermission?.(mode)) === "granted";
}

/**
 * Report changes under the directory as paths relative to it, or undefined when
 * the browser cannot say which paths changed. Resolves to the function that
 * stops watching, or to undefined where there is no `FileSystemObserver`.
 */
export async function observeDirectory(
    handle: DirectoryHandle,
    changed: (paths: string[] | undefined) => void,
): Promise<(() => void) | undefined> {
    const Observer = (globalThis as { FileSystemObserver?: FileSystemObserverConstructor }).FileSystemObserver;
    if (Observer === undefined) return undefined;
    const observer = new Observer((records) => {
        const paths: string[] = [];
        for (const record of records) {
            if (record.type === "unknown" || record.type === "errored") {
                changed(undefined);
                return;
            }
            paths.push(record.relativePathComponents.join("/"));
            if (record.relativePathMovedFrom) paths.push(record.relativePathMovedFrom.join("/"));
        }
        changed(paths);
    });
    await observer.observe(handle, { recursive: true });
    return () => observer.disconnect();
}

/** The part of `FileSystemObserver` used here, which TypeScript's DOM library does not declare. */
type FileSystemObserverConstructor = new (
    callback: (
        records: {
            type: "appeared" | "disappeared" | "modified" | "moved" | "unknown" | "errored";
            relativePathComponents: string[];
            relativePathMovedFrom?: string[];
        }[],
    ) => void,
) => {
    observe(handle: DirectoryHandle, options: { recursive: boolean }): Promise<void>;
    disconnect(): void;
};

type Picker = (options: { id?: string; mode?: "read" | "readwrite" }) => Promise<DirectoryHandle>;

/**
 * `showDirectoryPicker`, which TypeScript's DOM library does not declare, and
 * which is only there in Chromium browsers.
 */
function picker(): Picker | undefined {
    return (globalThis as { showDirectoryPicker?: Picker }).showDirectoryPicker;
}
